<template>
  <div
    class="live-alerts-container fill-height"
    style="margin-top: 5px;"
  >
    <div class="alerts-header">
      <div class="d-flex align-center">
        <span class="bullet-point">•</span>
        <h3 class="alerts-title">
          {{ $t('Dashboard.Banking.MarketData.LiveAlerts.Title') }}
        </h3>
      </div>
      <v-btn
        class="view-all-btn"
        color="synthesis-ui-green-02"
        text
        small
        @click="onViewAllClick"
      >
        {{ $t('Dashboard.Banking.MarketData.LiveAlerts.ViewAll') }}
      </v-btn>
    </div>

    <div :class="['alerts-content', blurBoxes ? 'blur-box' : '']">
      <div
        v-for="(alert, index) in alertSummaries"
        :key="`alert-${index}`"
        class="alert-card"
        @click="onAlertClick(alert)"
      >
        <div class="alert-icon-container">
          <div :class="['alert-icon', alert.color]">
            <v-icon
              size="16"
            >
              mdi-bell
            </v-icon>
          </div>
        </div>

        <div class="alert-info">
          <div class="alert-title">
            {{ alert.title }}
          </div>
          <div class="alert-subtitle">
            {{ alert.subtitle }}
          </div>
        </div>

        <div class="alert-values">
          <div class="alert-value">
            {{ alert.value }}
          </div>
          <div :class="['alert-change', alert.changeColor]">
            {{ alert.change }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import DashboardAlertsListItemResource from '@/api/resources/dashboard/DashboardAlertsListItemResource'
import { collect } from '@/lib/utils/array'

export default {
  name     : 'LiveAlerts',
  dataStore: {
    BankingDashboard: 'BankingDashboard.Data'
  },
  data () {
    return {
      blurBoxes: false
    }
  },
  computed: {
    accountId () {
      return this.user?.selectedAccountId || null
    },
    alertSummaries () {
      const hasAlerts = this.BankingDashboard?.marketDataDashboard?.alertSummaries?.length > 0
      const alertItems = collect(hasAlerts ? this.BankingDashboard?.marketDataDashboard?.alertSummaries ?? [] : this.getDummyData(), DashboardAlertsListItemResource)

      return this.formatAlerts(alertItems)
    }
  },
  watch: {
    BankingDashboard: {
      handler (newData) {
        if (newData && newData.marketDataDashboard) {
          const hasAlerts = newData.marketDataDashboard.alertSummaries?.length > 0
          this.blurBoxes = !hasAlerts
        }
      },
      deep     : true,
      immediate: true
    }
  },
  methods: {
    formatAlerts (alertItems) {
      return alertItems.map((item, index) => {
        // Determine color based on priority or alert type
        let color = 'white' // default

        if (item.Priority === 'High') {
          color = 'red'
        } else if (item.Priority === 'Medium') {
          color = 'orange'
        } else if (item.Priority === 'Low') {
          color = 'green'
        } else {
          // Fallback to cycling colors if no priority
          const colors = ['red', 'orange', 'green']
          color = colors[index % colors.length]
        }

        // Format the value based on alert type - using real API data
        const value = item.Value || '0'

        // Format percentage with 2 decimal places and proper color
        let change = '0%'
        let changeColor = 'negative'

        if (item.CurrentDistance !== undefined && item.CurrentDistance !== null) {
          const percentage = (item.CurrentDistance * 100).toFixed(2)
          // Remove .00 if it's a whole number
          const formattedPercentage = percentage.replace(/\.00$/, '')
          change = `${formattedPercentage}%`
          changeColor = item.CurrentDistance > 0 ? 'positive' : 'negative'
        } else if (item.ChangeValue) {
          // Extract number from existing ChangeValue and format to 2 decimals
          const match = item.ChangeValue.match(/([+-]?\d*\.?\d+)/)
          if (match) {
            const num = parseFloat(match[1])
            const formattedNum = num.toFixed(2).replace(/\.00$/, '')
            change = `${formattedNum}%`
            changeColor = num > 0 ? 'positive' : 'negative'
          }
        }

        return {
          title      : item.Name || 'Unknown Asset',
          subtitle   : item.Type || 'Alert',
          value      : value,
          change     : change,
          changeColor: changeColor,
          color      : color,
          id         : item.Id,
          priority   : item.Priority
        }
      })
    },
    onAlertClick (alert) {
      if (alert && alert.id) {
        this.$router.push({
          name  : 'AlertShow',
          params: {
            id     : alert.id,
            alertId: alert.id
          }
        })
      }
    },
    onViewAllClick () {
      this.$router.push({
        name: 'Alerts'
      })
    },
    getDummyData () {
      return [
        {
          dummy          : true,
          id             : '44e08233-5c02-4849-a1b9-aa271706554b',
          name           : 'EUR-USD',
          priority       : 'High',
          alertType      : 'Asset_Price',
          threshold      : 1200000,
          conditionType  : 'Upper_Limit',
          currentDistance: 0.0215
        },
        {
          dummy          : true,
          id             : '18d7ed96-4e26-4f9e-9a78-b4d2f06a4205',
          name           : 'Bank Portfolio',
          priority       : 'Medium',
          alertType      : 'Portfolio_MTM',
          threshold      : 2620765,
          conditionType  : 'Upper_Limit',
          currentDistance: 0
        },
        {
          dummy          : true,
          id             : '81e4f270-9388-4911-a447-d33f24903f2f',
          name           : 'Interest Rate Swap',
          priority       : 'Low',
          alertType      : 'Asset_Prices',
          threshold      : 100000,
          conditionType  : 'Upper_Limit',
          currentDistance: -0.10
        }
      ]
    }
  }
}
</script>

<style scoped>
.live-alerts-container {
  padding: 20px;
}

.alerts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.bullet-point {
  color: var(--v-synthesis-ui-grey-01-base);
  font-size: 16px;
  font-weight: bold;
  margin-right: 8px;
  display: inline-block;
}

.alerts-title {
  font-family: Inter, Aeonik, Roboto, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
  color: var(--v-synthesis-ui-grey-01-base);
}

.view-all-btn {
  font-size: 12px;
  font-weight: 400;
  text-transform: none;
  min-width: auto;
  padding: 0 8px;
  text-decoration: underline;
}

.alerts-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
}

.alert-card {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 8px 12px;
  background-color: var(--v-synthesis-ui-grey-06-base);
  border-radius: 8px;
  box-shadow: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.alert-card:hover {
  background-color: var(--v-synthesis-ui-grey-07-base);
}

.alert-icon-container {
  margin-right: 12px;
}

.alert-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
}

.alert-icon.red {
  background-color: var(--v-synthesis-ui-red-07-base) !important;
}

.alert-icon.red .v-icon {
  color: var(--v-synthesis-ui-red-01-base) !important;
  font-weight: 900;
}

.alert-icon.orange {
  background-color: var(--v-synthesis-ui-yellow-07-base) !important;
}

.alert-icon.orange .v-icon {
  color: var(--v-synthesis-ui-yellow-01-base) !important;
  font-weight: 900;
}

.alert-icon.green {
  background-color: var(--v-synthesis-ui-green-07-base) !important;
}

.alert-icon.green .v-icon {
  color: var(--v-synthesis-ui-green-01-base) !important;
  font-weight: 900;
}

.alert-icon.white {
  background-color: var(--v-synthesis-ui-grey-08-base) !important;
}

.alert-icon.white .v-icon {
  color: var(--v-synthesis-ui-grey-01-base) !important;
  font-weight: 900;
}

.alert-info {
  flex: 1;
}

.alert-title {
  font-family: Aeonik, Inter, Roboto, sans-serif;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  line-height: 130%;
  letter-spacing: 0.2px;
  color: var(--v-synthesis-brand-dark-blue-base);
  margin-bottom: 2px;
}

.alert-subtitle {
  font-family: Inter, Aeonik, Roboto, sans-serif;
  font-weight: 400;
  font-style: Regular;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0px;
  color: var(--v-synthesis-ui-grey-01-base);
}

.alert-values {
  text-align: right;
}

.alert-value {
  font-family: Aeonik, Inter, Roboto, sans-serif;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  line-height: 130%;
  letter-spacing: 0.2px;
  color: var(--v-synthesis-brand-dark-blue-base);
  margin-bottom: 2px;
}

.alert-change {
  font-size: 12px;
  font-weight: 500;
}

.alert-change.positive {
  color: var(--v-synthesis-ui-green-01-base);
}

.alert-change.negative {
  color: var(--v-synthesis-ui-red-02-base);
}

.blur-box {
  filter: blur(5px);
  pointer-events: none;
}
</style>
